<template>
  <!-- The birthday step's tutorial, made visual (recipient polish 2026-11-03,
       DESIGN.md §20.1; prototype `tutorialHTML`). The built "What's this?"
       explainer's three beats and dots (BirthExplainer), now inline above the
       form: one picture each, a few words each, advancing by themselves.
         1 the art of the signs: cross-fades while no date is in; settles on
           the sign once a valid date is in ("You're a Pisces.")
         2 your sky beside the sender's, three coloured threads between
         3 the chart preview: the three threads, blank
       With reduced motion the beats stay still; the dots step. -->
  <div class="bb" :class="{ still: reduced }" aria-roledescription="carousel" aria-label="What your birthday shows" data-testid="birth-beats">
    <div :key="beat" class="bb-beat" data-testid="bb-beat" :data-beat="beat">
      <template v-if="beat === 0">
        <div v-if="mine" class="bb-art in" data-testid="bb-sign">
          <img v-if="artFor(mine)" :src="artFor(mine)" :alt="mine" />
          <span v-else class="bb-glyph">{{ glyphFor(mine) }}</span>
        </div>
        <div v-else class="bb-art bb-cycle" aria-hidden="true">
          <template v-for="(s, i) in cycle" :key="s">
            <img v-if="artFor(s)" :src="artFor(s)" alt="" :style="{ animationDelay: (i * 1.5) + 's' }" />
            <span v-else class="bb-glyph" :style="{ animationDelay: (i * 1.5) + 's' }">{{ glyphFor(s) }}</span>
          </template>
        </div>
        <p>{{ mine ? `You're ${article(mine)} ${mine}.` : 'Your birthday shows your sign.' }}</p>
      </template>
      <template v-else-if="beat === 1">
        <div class="bb-pair">
          <div class="bb-sky me">
            <template v-if="mine"><img v-if="artFor(mine)" :src="artFor(mine)" alt="" /><span v-else class="bb-glyph sm">{{ glyphFor(mine) }}</span></template>
            <span v-else class="bb-q">?</span>
          </div>
          <svg viewBox="0 0 120 40" aria-hidden="true">
            <path v-for="(y, n) in [8, 20, 32]" :key="n" :d="`M0 ${y} Q60 ${y + 6} 120 ${y}`" :stroke="THREAD_COLORS[n]" stroke-width="2"
                  fill="none" opacity=".85" />
          </svg>
          <div class="bb-sky">
            <template v-if="theirs"><img v-if="artFor(theirs)" :src="artFor(theirs)" :alt="theirs" /><span v-else class="bb-glyph sm">{{ glyphFor(theirs) }}</span></template>
            <span v-else class="bb-q">✦</span>
          </div>
        </div>
        <p>Your sky beside {{ possessive(sender) }}.</p>
      </template>
      <template v-else>
        <svg class="bb-chart" :viewBox="`0 0 ${blank.W} ${blank.H}`" aria-hidden="true">
          <template v-for="(r, i) in blank.rows" :key="i">
            <path :d="r.d" fill="none" stroke="rgba(203,184,238,.5)" stroke-width="1.4" stroke-dasharray="2 5" />
            <circle :cx="blank.L" :cy="r.y" r="4" fill="#cbb8ee" opacity=".7" /><circle :cx="blank.R" :cy="r.y" r="4" fill="#cbb8ee" opacity=".7" />
          </template>
        </svg>
        <p>Then where the two of you meet.</p>
      </template>
    </div>
    <div class="bb-dots">
      <button v-for="i in beats" :key="i" type="button" :class="{ on: i - 1 === beat }" :aria-label="`Beat ${i} of ${beats}`"
              :aria-current="i - 1 === beat ? 'step' : null" data-testid="bb-dot" @click="go(i - 1)"></button>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { SIGNS, GLYPHS, sunSignOfDate, article } from '@/utils/birth-ask'
import { THREAD_COLORS, threadLayout, possessive } from '@/utils/you-and-them'

const props = defineProps({
  // The sender as signed ("Chris Lee", "your mom"), and their sign if known.
  sender: { type: String, default: '' },
  theirSign: { type: String, default: '' },
  // A valid birth date (ISO) once it is in: the beats settle on its sign.
  date: { type: String, default: '' },
  // Mystic Aida's sign art, { pisces: url } (optional; the glyph otherwise).
  signArt: { type: Object, default: () => ({}) },
  reduced: { type: Boolean, default: false },
  // Each beat's time on screen.
  everyMs: { type: Number, default: 3400 },
})

const beats = 3
const beat = ref(0)
const mine = computed(() => (props.date ? sunSignOfDate(props.date) || '' : ''))
const theirs = computed(() => (SIGNS.includes(props.theirSign) ? props.theirSign : ''))
const cycle = ['Aries', 'Leo', 'Libra', 'Pisces']
const artFor = (s) => (props.signArt && props.signArt[String(s || '').toLowerCase()]) || ''
const glyphFor = (s) => GLYPHS[SIGNS.indexOf(s)] || '✦'
const blank = threadLayout([], { narrow: true })

let timer = null
function stop () { if (timer) { clearInterval(timer); timer = null } }
function start () {
  stop()
  if (props.reduced || !(props.everyMs > 0)) return
  timer = setInterval(() => { beat.value = (beat.value + 1) % beats }, props.everyMs)
}
// A tap on a dot shows that beat and holds it.
function go (i) { beat.value = i; stop() }
onMounted(start)
onBeforeUnmount(stop)
defineExpose({ beat, go })
</script>

<style scoped>
.bb { max-width: 520px; margin: 0 auto 14px; padding: 14px 14px 10px; border-radius: 18px; border: 1px solid rgba(240,217,163,.3);
  background: radial-gradient(110% 90% at 50% 20%, rgba(123,75,176,.35), rgba(20,7,31,.9) 70%); text-align: center; }
.bb-beat { height: 230px; min-height: 230px; display: flex; flex-direction: column; align-items: center; justify-content: center;
  animation: bb-in .5s ease both; }
.still .bb-beat { animation: none; }
@keyframes bb-in { from { opacity: 0; } to { opacity: 1; } }
.bb-beat p { margin: 10px 0 0; font: 500 21px 'Letter Serif', 'Cormorant Garamond', Georgia, serif; color: #fff4dc; }
.bb-art { position: relative; width: 170px; height: 170px; border-radius: 50%; overflow: hidden; border: 2px solid rgba(240,217,163,.6);
  box-shadow: 0 0 40px rgba(240,217,163,.25); display: grid; place-items: center;
  background: radial-gradient(circle, rgba(240,217,163,.18), rgba(123,75,176,.2) 60%, #1d0f3a); }
.bb-art img { width: 100%; height: 100%; object-fit: cover; }
.bb-glyph { font-family: 'Apple Symbols', 'Segoe UI Symbol', 'Noto Sans Symbols 2', serif; font-variant-emoji: text; font-size: 78px;
  color: #f0d9a3; line-height: 1; }
.bb-glyph.sm { font-size: 46px; }
.bb-cycle > img, .bb-cycle > .bb-glyph { position: absolute; inset: 0; display: grid; place-items: center; opacity: 0;
  animation: bb-cycle 6s infinite; }
.still .bb-cycle > img, .still .bb-cycle > .bb-glyph { animation: none; }
.still .bb-cycle > :last-child { opacity: 1; }
@keyframes bb-cycle { 0% { opacity: 0; } 8%, 25% { opacity: 1; } 33%, 100% { opacity: 0; } }
.bb-pair { display: grid; grid-template-columns: 100px 100px 100px; align-items: center; }
.bb-pair svg { width: 100px; }
.bb-sky { width: 100px; height: 100px; border-radius: 50%; overflow: hidden; border: 2px solid rgba(240,217,163,.6); display: grid;
  place-items: center; background: #1d0f3a; }
.bb-sky.me { border-color: rgba(233,220,255,.6); }
.bb-sky img { width: 100%; height: 100%; object-fit: cover; }
.bb-q { font: 500 44px 'Letter Serif', Georgia, serif; color: #cbb8ee; }
/* Held inside the beat's height: the chart beat was ~90 px taller, so the
   form below jumped between beats (walk 2026-11-03 B). */
.bb-chart { width: 100%; max-width: 320px; max-height: 170px; }
.bb-dots { display: flex; justify-content: center; gap: 4px; margin-top: 6px; }
.bb-dots button { position: relative; width: 24px; height: 24px; padding: 0; border: 0; background: none; cursor: pointer; }
.bb-dots button::after { content: ''; position: absolute; inset: 7px; border-radius: 50%; background: rgba(185,166,214,.35); }
.bb-dots button.on::after { background: #f0d9a3; }
.bb-dots button:focus-visible { outline: 2px solid #f0d9a3; outline-offset: 1px; border-radius: 50%; }
@media (max-width: 899px) {
  .bb-pair { grid-template-columns: 86px 76px 86px; } .bb-sky { width: 86px; height: 86px; } .bb-pair svg { width: 76px; }
  .bb-beat { height: 210px; min-height: 210px; } .bb-art { width: 150px; height: 150px; }
  .bb-chart { max-height: 150px; }
}
@media (prefers-reduced-motion: reduce) { .bb-beat, .bb-cycle > img, .bb-cycle > .bb-glyph { animation: none; } }
</style>
